import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import { useLocationDetection } from "@/hooks/useLocationDetection";
import { CpvBatchProvider } from "@/hooks/useCpvBatch";
import CookieConsent from "@/components/CookieConsent";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import Search from "./pages/Search";
import Monitoring from "./pages/Monitoring";
import Results from "./pages/Results";
import Clients from "./pages/Clients";
import Users from "./pages/Users";
import Settings from "./pages/Settings";
import Pricing from "./pages/Pricing";
import AdminCpv from "./pages/AdminCpv";
import Releases from "./pages/Releases";
import AdminUsageStats from "./pages/AdminUsageStats";
import AdminTickets from "./pages/AdminTickets";
import AdminClientSearch from "./pages/AdminClientSearch";
import AdminActivityMonitoring from "./pages/AdminActivityMonitoring";
import NotFound from "./pages/NotFound";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import TermsConditions from "./pages/TermsConditions";
import DataSource from "./pages/DataSource";
import Support from "./pages/Support";
import CpvSearchPage from "./pages/CpvSearch";
import EuTenderMonitoring from "./pages/EuTenderMonitoring";
import TedProcurementAlerts from "./pages/TedProcurementAlerts";
import EuropeanPublicProcurementSoftware from "./pages/EuropeanPublicProcurementSoftware";
import MultilingualAccess from "./pages/MultilingualAccess";
import ComplianceAndSecurity from "./pages/ComplianceAndSecurity";
import AiResultsAnalysis from "./pages/AiResultsAnalysis";
import DataAnalysis from "./pages/DataAnalysis";
// Italian landing pages
import MonitoraggioBandiUe from "./pages/it/MonitoraggioBandiUe";
import AlertBandiTed from "./pages/it/AlertBandiTed";
import SoftwareAppaltiPubbliciEuropei from "./pages/it/SoftwareAppaltiPubbliciEuropei";
import AccessoMultilingue from "./pages/it/AccessoMultilingue";
import ConformitaSicurezza from "./pages/it/ConformitaSicurezza";
import AnalisiRisultatiAi from "./pages/it/AnalisiRisultatiAi";
import Prezzi from "./pages/it/Prezzi";
import FonteDeiDati from "./pages/it/FonteDeiDati";
// Italian guide pages
import ConsultareTed from "./pages/it/guide/ConsultareTed";
import CodiciCpv from "./pages/it/guide/CodiciCpv";
import BandiVsPreinformazione from "./pages/it/guide/BandiVsPreinformazione";
import FiltriRicercaAvanzata from "./pages/it/guide/FiltriRicercaAvanzata";

const queryClient = new QueryClient();

// Location detector component to initialize language on app load
function LocationDetector({ children }: { children: React.ReactNode }) {
  useLocationDetection();
  return <>{children}</>;
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SubscriptionProvider>
          <LocationDetector>
            <CpvBatchProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/search" element={<Search />} />
                    <Route path="/monitoring" element={<Monitoring />} />
                    <Route path="/results" element={<Results />} />
                    
                    <Route path="/clients" element={<Clients />} />
                    <Route path="/users" element={<Users />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/pricing" element={<Pricing />} />
                    <Route path="/admin/cpv" element={<AdminCpv />} />
                    <Route path="/releases" element={<Releases />} />
                    <Route path="/admin/usage-stats" element={<AdminUsageStats />} />
                    <Route path="/admin/tickets" element={<AdminTickets />} />
                    <Route path="/admin/client-search" element={<AdminClientSearch />} />
                    <Route path="/admin/activity" element={<AdminActivityMonitoring />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/cookie-policy" element={<CookiePolicy />} />
                    <Route path="/terms-conditions" element={<TermsConditions />} />
                    <Route path="/data-source" element={<DataSource />} />
                    <Route path="/cpv-search" element={<CpvSearchPage />} />
                    <Route path="/support" element={<Support />} />
                    <Route path="/eu-tender-monitoring" element={<EuTenderMonitoring />} />
                    <Route path="/ted-procurement-alerts" element={<TedProcurementAlerts />} />
                    <Route path="/european-public-procurement-software" element={<EuropeanPublicProcurementSoftware />} />
                    <Route path="/multilingual-access" element={<MultilingualAccess />} />
                    <Route path="/compliance-and-security" element={<ComplianceAndSecurity />} />
                    <Route path="/ai-results-analysis" element={<AiResultsAnalysis />} />
                    <Route path="/data-analysis" element={<DataAnalysis />} />
                    {/* Italian landing pages */}
                    <Route path="/it/monitoraggio-bandi-ue" element={<MonitoraggioBandiUe />} />
                    <Route path="/it/alert-bandi-ted" element={<AlertBandiTed />} />
                    <Route path="/it/software-appalti-pubblici-europei" element={<SoftwareAppaltiPubbliciEuropei />} />
                    <Route path="/it/accesso-multilingue" element={<AccessoMultilingue />} />
                    <Route path="/it/conformita-sicurezza" element={<ConformitaSicurezza />} />
                    <Route path="/it/analisi-risultati-ai" element={<AnalisiRisultatiAi />} />
                    <Route path="/it/prezzi" element={<Prezzi />} />
                    <Route path="/it/fonte-dei-dati" element={<FonteDeiDati />} />
                    {/* Italian guide pages */}
                    <Route path="/it/guide/consultare-ted" element={<ConsultareTed />} />
                    <Route path="/it/guide/codici-cpv" element={<CodiciCpv />} />
                    <Route path="/it/guide/bandi-vs-preinformazione" element={<BandiVsPreinformazione />} />
                    <Route path="/it/guide/filtri-ricerca-avanzata" element={<FiltriRicercaAvanzata />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  <CookieConsent />
                </BrowserRouter>
              </TooltipProvider>
            </CpvBatchProvider>
          </LocationDetector>
        </SubscriptionProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
